import {
  type CanvasElementInputBinding,
  type DataBindingSourceType,
  type ElementOutputUpdateSource,
  type ElementQueryState,
  type RenderElementDataRowContext,
} from '@blaze-shared/elements-domain';
import { type BlazeSchema, type DatasetColumn } from '@blaze-shared/schema-domain';

import {
  type CanvasElementId,
  type CanvasElementType,
  type CanvasNamespaceId,
} from './canvas-element-id';
import { type CanvasElementDefinitions } from './definition';
import { type CanvasElementPathNode } from './path';
import { type CanvasElementProperties, type HeightSettings, type WidthSettings } from './props';
import { type SlotIdOf, type Slots } from './slots';

export const BLAZE_CANVAS_SELECTOR = 'blaze-canvas';

export * from './canvas-element-id';

/**
 * Describes any element placed on the canvas.
 * This type is generated by combining all definitions provided in the {@link CanvasElementDefinitions} type.
 */
export type CanvasElement = CanvasElementFrom<keyof CanvasElementDefinitions>;

/** ${namespaceId}_${innerId} */
export type CanvasElementInnerId = number;

/**
 * Used as a common type for {@link CanvasElementBase} and {@link CanvasElementDefinition}.
 */
export interface CanvasElementBase<
  Type extends CanvasElementType,
  SlotsType = Slots<SlotIdOf<Type>>,
> {
  type: Type;
  placeholderMode?: 'target' | 'option';
  width: WidthSettings;
  height?: HeightSettings;
  slots?: SlotsType;
  description?: string;
  overrides?: CanvasElementOverrides;
}

/**
 * Used to generate the type of a specific type of CanvasElement.
 *
 * ```
 * let containerElement: CanvasElementFrom<'container'>;
 * ```
 *
 * @param id - Unique element Id
 * @param ns - Unique namespace Id (nested layouts)
 * @param name - Unique element name (for example: 'Button 2')
 * @param parentNode - id and slot of the parent where the element is placed
 */
export type CanvasElementFrom<T extends CanvasElementType> = CanvasElementBase<T> & {
  id: CanvasElementId;
  ns: CanvasNamespaceId;
  /** WARNING name may not be unique*/
  name: string;
  parentNode?: CanvasElementPathNode;
  description?: string;
  version?: number;
} & CanvasElementDefinitions[T];

/** Additional data for an element {@link RenderElementDirective} */
export type CanvasElementDataInput<TData = any> = Partial<
  Record<CanvasElementInputBinding, CanvasElementDataInputPayload<TData>>
>;

export interface CanvasElementDataInputPayload<TData = any> {
  key: CanvasElementInputBinding;
  sourceType: DataBindingSourceType;
  data: TData;
  schema?: BlazeSchema;
  column?: DatasetColumn | undefined;
  updateSource?: ElementOutputUpdateSource;
  elementQuery?: ElementQueryState;
  rowContext?: RenderElementDataRowContext;
}

export type CanvasElementOverrideTarget = 'tablet' | 'mobile';

/**
 * Element can have its properties overridden for specific targets.
 * Currently, only props can be overridden.
 * In the future, we may support overriding other properties as well.
 */
export interface CanvasElementOverride<
  P extends CanvasElementProperties | undefined = CanvasElementProperties,
> {
  props?: P;
}

export type CanvasElementOverrides<
  P extends CanvasElementProperties | undefined = CanvasElementProperties,
> = {
  [Target in CanvasElementOverrideTarget]?: CanvasElementOverride<P>;
};
